// Auto-install Claude Code CLI qua `npm install -g @anthropic-ai/claude-code`.
// Stream stdio để user thấy npm progress. Sau khi cài xong → re-detect verify PATH.
// Throws InstallClaudeCodeError với hint cụ thể cho EACCES / ENOSPC / generic.
import { spawnSync } from "node:child_process";
import {
  detectClaudeCodeInstallation,
  invalidateClaudeCodeInstallationCache,
} from "./detect-claude-code-installation.js";
import { log } from "./terminal-logger.js";

// 5 phút — npm global install có thể chậm trên mạng yếu (registry resolve + download).
const NPM_INSTALL_TIMEOUT_MS = 5 * 60 * 1000;

// Package name chính thức của Anthropic Claude Code CLI.
const CLAUDE_CODE_PACKAGE = "@anthropic-ai/claude-code";

export type InstallClaudeCodeReason =
  | "permission-denied" // EACCES → cần sudo hoặc fix npm prefix
  | "disk-full" // ENOSPC
  | "timeout" // > 5 phút
  | "binary-not-in-path" // install OK nhưng PATH chưa refresh
  | "generic"; // exit non-zero khác

export class InstallClaudeCodeError extends Error {
  reason: InstallClaudeCodeReason;
  exitCode: number | null;
  constructor(reason: InstallClaudeCodeReason, message: string, exitCode: number | null = null) {
    super(message);
    this.name = "InstallClaudeCodeError";
    this.reason = reason;
    this.exitCode = exitCode;
  }
}

// Phân loại exit từ npm install thành reason cụ thể để caller branch hint.
// Hint hiển thị qua message, không log trực tiếp ở đây (separation of concerns).
function classifyNpmFailure(exitCode: number | null, stderrSample: string): InstallClaudeCodeError {
  const stderr = stderrSample.toLowerCase();
  if (stderr.includes("eacces") || stderr.includes("permission denied")) {
    return new InstallClaudeCodeError(
      "permission-denied",
      `npm install -g cần quyền. Thử: sudo npm install -g ${CLAUDE_CODE_PACKAGE} hoặc fix npm prefix (npm config set prefix ~/.npm-global).`,
      exitCode,
    );
  }
  if (stderr.includes("enospc") || stderr.includes("no space")) {
    return new InstallClaudeCodeError(
      "disk-full",
      "Đĩa đầy. Free disk space rồi thử lại.",
      exitCode,
    );
  }
  return new InstallClaudeCodeError(
    "generic",
    `npm install thất bại (exit ${exitCode ?? "null"}). Xem log npm phía trên.`,
    exitCode,
  );
}

// Main API: cài Claude Code + verify. Throws InstallClaudeCodeError nếu fail.
// Return version đã detect (luôn non-null nếu success vì re-detect verify SemVer).
export function installClaudeCodeViaNpm(): { version: string | null; path: string } {
  log.info("Đang cài Claude Code qua npm (có thể mất 1-2 phút)...");

  // Pipe stderr để classify error sau, nhưng inherit stdout cho user thấy progress.
  // Note: stdio mixed array — [stdin, stdout, stderr].
  const result = spawnSync("npm", ["install", "-g", CLAUDE_CODE_PACKAGE], {
    stdio: ["inherit", "inherit", "pipe"],
    timeout: NPM_INSTALL_TIMEOUT_MS,
    encoding: "utf8",
  });

  // Timeout case — Node set signal SIGTERM khi timeout.
  if (result.signal === "SIGTERM") {
    throw new InstallClaudeCodeError(
      "timeout",
      `npm install timeout sau ${NPM_INSTALL_TIMEOUT_MS / 1000}s. Check mạng rồi thử lại.`,
      null,
    );
  }

  if (result.status !== 0) {
    // In stderr ra để user thấy lỗi gốc trước khi throw.
    if (result.stderr) process.stderr.write(result.stderr);
    throw classifyNpmFailure(result.status, result.stderr || "");
  }

  // v1.14.1 BUG FIX: Invalidate memoize cache TRƯỚC re-detect.
  // Cùng bug như install-gitnexus-via-npm: cache pre-install giữ {installed: false}
  // → re-detect trả false dù binary đã thực sự install.
  invalidateClaudeCodeInstallationCache();

  // Re-detect — npm install OK không đồng nghĩa binary đã trong PATH (PATH cache).
  const probe = detectClaudeCodeInstallation();
  if (!probe.installed || !probe.path) {
    throw new InstallClaudeCodeError(
      "binary-not-in-path",
      "npm cài xong nhưng `claude` không trong PATH. Reload shell (source ~/.zshrc) hoặc thêm npm global bin vào PATH.",
      null,
    );
  }

  log.success(`Đã cài Claude Code${probe.version ? ` v${probe.version}` : ""} tại ${probe.path}`);
  return { version: probe.version, path: probe.path };
}
